import React, { useContext, useEffect, useMemo, useState } from "react";
import {
	Alert,
	Badge,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Col,
	Form,
	FormGroup,
	Input,
	Label,
	ListGroup,
	ListGroupItem,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Spinner,
	Table,
} from "reactstrap";
import { RestContext } from "../../App";
import { ADMIN_URL, STACKPLOT_URL, UTIL_URL } from "../../serverConfig";

const API = {
	batches: STACKPLOT_URL + "/batch/list",
	batch: (id) => STACKPLOT_URL + `/batch/${id}`,

	updateBatch: STACKPLOT_URL + `/batch`,
	deleteBatch: (id) => STACKPLOT_URL + `/batch/delete/${id}`,

	stackPlots: STACKPLOT_URL + "/list/raw",

	usersPlaceholder: UTIL_URL + "/users/ars",
	batchPermissions: (batchId) =>
		STACKPLOT_URL + `/batch/${batchId}/permissions`,
	saveBatchPermissions: (batchId) =>
		STACKPLOT_URL + `/batch/${batchId}/permissions/save`,
};

const emptyBatch = {
	batchId: null,
	batchName: "",
	owner: "",
	isPublic: false,
	plots: [],
	accessLevel: "O",
};

export default function StackBatchEditor() {
	const { sendGetRequest, sendPostRequest, sendDeleteRequest } =
		useContext(RestContext);

	const [batches, setBatches] = useState([]);
	const [selectedBatchId, setSelectedBatchId] = useState(null);
	const [batch, setBatch] = useState(emptyBatch);
	const [originalBatch, setOriginalBatch] = useState(emptyBatch);

	const [loadingList, setLoadingList] = useState(false);
	const [loadingBatch, setLoadingBatch] = useState(false);
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);

	const [error, setError] = useState("");
	const [success, setSuccess] = useState("");

	const [plotModalOpen, setPlotModalOpen] = useState(false);
	const [plotSearch, setPlotSearch] = useState("");
	const [availablePlots, setAvailablePlots] = useState([]);
	const [loadingPlots, setLoadingPlots] = useState(false);

	const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
	const [allUsers, setAllUsers] = useState([]);
	const [batchPermissions, setBatchPermissions] = useState([]);
	const [userSearch, setUserSearch] = useState("");
	const [loadingPermissions, setLoadingPermissions] = useState(false);
	const [savingPermissions, setSavingPermissions] = useState(false);

	const [collapsedAgencies, setCollapsedAgencies] = useState({});

	const [batchSearch, setBatchSearch] = useState("");
	const [showOnlyOwnedBatches, setShowOnlyOwnedBatches] = useState(false);

	const dirty = useMemo(
		() => JSON.stringify(batch) !== JSON.stringify(originalBatch),
		[batch, originalBatch],
	);

	useEffect(() => {
		loadBatches();
	}, []);

	useEffect(() => {
		const warn = (event) => {
			if (!dirty) return;
			event.preventDefault();
			event.returnValue = "";
		};

		window.addEventListener("beforeunload", warn);
		return () => window.removeEventListener("beforeunload", warn);
	}, [dirty]);

	function getErrorMessage(error, fallback) {
		return error?.response?.data?.message || error?.message || fallback;
	}

	function normalizeBatch(data) {
		return {
			batchId: data?.batchId ?? null,
			batchName: data?.batchName ?? "",
			owner: data?.owner ?? "",
			isPublic: Boolean(data?.isPublic || data?.public),
			accessLevel: data?.accessLevel ?? null,
			plots: Array.isArray(data?.plots) ? data.plots : [],
		};
	}

	function loadBatches() {
		setError("");
		setLoadingList(true);

		sendGetRequest(
			API.batches,
			(response) => {
				setBatches(response.data || []);
				setLoadingList(false);
			},
			(error) => {
				setError(
					getErrorMessage(error, "Failed to load stack batches."),
				);
				setLoadingList(false);
			},
		);
	}

	function selectBatch(id) {
		if (dirty && !window.confirm("Discard unsaved changes?")) return;

		setError("");
		setSuccess("");
		setLoadingBatch(true);
		setSelectedBatchId(id);

		sendGetRequest(
			API.batch(id),
			(response) => {
				const normalized = normalizeBatch(response.data);
				setBatch(normalized);
				setOriginalBatch(normalized);
				setLoadingBatch(false);
			},
			(error) => {
				setError(getErrorMessage(error, "Failed to load stack batch."));
				setLoadingBatch(false);
			},
		);
	}

	function canEditCurrentBatch() {
		return batch.accessLevel === "O" || batch.accessLevel === "W";
	}

	function canDeleteCurrentBatch() {
		console.log(batch);
		return batch.accessLevel === "O";
	}

	function toggleAgencyCollapsed(agencyName) {
		setCollapsedAgencies((prev) => ({
			...prev,
			[agencyName]: !prev[agencyName],
		}));
	}

	function getGroupedFilteredPlots() {
		const searchTerms = plotSearch
			.trim()
			.toLowerCase()
			.split(/\s+/)
			.filter(Boolean);

		const filtered = availablePlots
			.filter((plot) => {
				const searchableText = [
					plot.plotName,
					plot.siteAbbr,
					plot.precision,
					plot.dataType,
					plot.agencyName,
				]
					.filter(Boolean)
					.join(" ")
					.toLowerCase();

				return (
					searchTerms.length === 0 ||
					searchTerms.every((term) => searchableText.includes(term))
				);
			})
			.sort((a, b) => {
				const siteCompare = (a.siteAbbr || "").localeCompare(
					b.siteAbbr || "",
					undefined,
					{ sensitivity: "base" },
				);

				if (siteCompare !== 0) return siteCompare;

				return (a.plotName || "").localeCompare(
					b.plotName || "",
					undefined,
					{
						sensitivity: "base",
					},
				);
			});

		const grouped = filtered.reduce((groups, plot) => {
			const agencyName = plot.agencyName || "Other";

			if (!groups[agencyName]) {
				groups[agencyName] = [];
			}

			groups[agencyName].push(plot);
			return groups;
		}, {});

		return Object.entries(grouped).sort(([agencyA], [agencyB]) => {
			const aIsOther = agencyA === "Other";
			const bIsOther = agencyB === "Other";

			if (aIsOther && !bIsOther) return 1;
			if (!aIsOther && bIsOther) return -1;

			return agencyA.localeCompare(agencyB, undefined, {
				sensitivity: "base",
			});
		});
	}

	function getAccessBadge(accessLevel) {
		switch (accessLevel) {
			case "O":
				return <Badge color="primary">Owner</Badge>;
			case "W":
				return <Badge color="success">Write</Badge>;
			case "R":
				return <Badge color="secondary">Read Only</Badge>;
			default:
				return <Badge color="light">No access</Badge>;
		}
	}

	function newBatch() {
		if (dirty && !window.confirm("Discard unsaved changes?")) return;

		setSelectedBatchId(null);
		setBatch(emptyBatch);
		setOriginalBatch(emptyBatch);
		setError("");
		setSuccess("");
	}

	function updateField(field, value) {
		setBatch((prev) => ({
			...prev,
			[field]: value,
		}));
	}

	function removePlot(stackPlotId) {
		setBatch((prev) => ({
			...prev,
			plots: prev.plots.filter(
				(plot) => plot.stackPlotId !== stackPlotId,
			),
		}));
	}

	function movePlot(index, direction) {
		setBatch((prev) => {
			const nextIndex = index + direction;

			if (nextIndex < 0 || nextIndex >= prev.plots.length) {
				return prev;
			}

			const plots = [...prev.plots];
			const temp = plots[index];
			plots[index] = plots[nextIndex];
			plots[nextIndex] = temp;

			return {
				...prev,
				plots,
			};
		});
	}

	function saveBatch() {
		if (!batch.batchName.trim()) {
			setError("Batch name is required.");
			return;
		}

		setError("");
		setSuccess("");
		setSaving(true);

		const payload = {
			...batch,
			plots: batch.plots.map((plot, index) => ({
				...plot,
				orderIndex: index + 1,
			})),
		};

		const url = API.updateBatch;

		sendPostRequest(
			url,
			payload,
			(response) => {
				const normalized = normalizeBatch(response.data || payload);

				setBatch(normalized);
				setOriginalBatch(normalized);
				setSelectedBatchId(normalized.batchId);
				setSuccess("Stack batch saved.");
				setSaving(false);

				loadBatches();
			},
			(error) => {
				setError(getErrorMessage(error, "Failed to save stack batch."));
				setSaving(false);
			},
		);
	}

	function deleteBatch() {
		if (!batch.batchId) return;
		if (!window.confirm(`Delete "${batch.batchName}"?`)) return;

		setError("");
		setSuccess("");
		setDeleting(true);

		sendDeleteRequest(
			API.deleteBatch(batch.batchId),
			() => {
				setBatch(emptyBatch);
				setOriginalBatch(emptyBatch);
				setSelectedBatchId(null);
				setSuccess("Stack batch deleted.");
				setDeleting(false);

				loadBatches();
			},
			(error) => {
				setError(
					getErrorMessage(error, "Failed to delete stack batch."),
				);
				setDeleting(false);
			},
		);
	}

	function openPlotModal() {
		setPlotModalOpen(true);
		setCollapsedAgencies({});
		loadAvailablePlots();
	}

	function loadAvailablePlots() {
		setError("");
		setLoadingPlots(true);

		sendGetRequest(
			API.stackPlots,
			(response) => {
				setAvailablePlots(
					Array.isArray(response.data) ? response.data : [],
				);
				setLoadingPlots(false);
			},
			(error) => {
				setError(getErrorMessage(error, "Failed to load stack plots."));
				setLoadingPlots(false);
			},
		);
	}

	function addPlot(plot) {
		const alreadyAdded = batch.plots.some(
			(existingPlot) => existingPlot.stackPlotId === plot.stackPlotId,
		);

		if (alreadyAdded) return;

		setBatch((prev) => ({
			...prev,
			plots: [...prev.plots, plot],
		}));
	}

	const filteredPlots = [...availablePlots]
		.sort((a, b) => {
			const siteCompare = (a.siteAbbr || "").localeCompare(
				b.siteAbbr || "",
			);

			if (siteCompare !== 0) {
				return siteCompare;
			}

			return (a.plotName || "").localeCompare(b.plotName || "");
		})
		.filter((plot) => {
			const query = plotSearch.toLowerCase();

			return (
				!query ||
				String(plot.plotName || "")
					.toLowerCase()
					.includes(query) ||
				String(plot.siteAbbr || "")
					.toLowerCase()
					.includes(query) ||
				String(plot.precision || "")
					.toLowerCase()
					.includes(query)
			);
		});

	function openPermissionsModal() {
		if (!batch.batchId) return;

		setPermissionsModalOpen(true);
		setUserSearch("");
		setLoadingPermissions(true);
		setError("");

		sendGetRequest(
			API.usersPlaceholder,
			(response) => {
				setAllUsers(Array.isArray(response.data) ? response.data : []);

				sendGetRequest(
					API.batchPermissions(batch.batchId),
					(permissionsResponse) => {
						setBatchPermissions(
							Array.isArray(permissionsResponse.data)
								? permissionsResponse.data
								: [],
						);
						setLoadingPermissions(false);
					},
					(error) => {
						setError(
							getErrorMessage(
								error,
								"Failed to load permissions.",
							),
						);
						setLoadingPermissions(false);
					},
				);
			},
			(error) => {
				setError(getErrorMessage(error, "Failed to load users."));
				setLoadingPermissions(false);
			},
		);
	}

	function getUserPermission(userId) {
		const permission = batchPermissions.find(
			(p) => p.id?.userId === userId,
		);

		return permission?.accessLevel || "";
	}

	function updateUserPermission(user, accessLevel) {
		const userId = user.userId || user.id;

		setBatchPermissions((prev) => {
			const withoutUser = prev.filter((p) => p.id?.userId !== userId);

			if (!accessLevel) {
				return withoutUser;
			}

			return [
				...withoutUser,
				{
					id: {
						batchId: batch.batchId,
						userId,
					},
					accessLevel,
				},
			];
		});
	}

	function savePermissions() {
		setSavingPermissions(true);
		setError("");

		sendPostRequest(
			API.saveBatchPermissions(batch.batchId),
			batchPermissions,
			() => {
				setSavingPermissions(false);
				setPermissionsModalOpen(false);
				setSuccess("Permissions saved.");
			},
			(error) => {
				setError(getErrorMessage(error, "Failed to save permissions."));
				setSavingPermissions(false);
			},
		);
	}

	const filteredUsers = [...allUsers]
		.sort((a, b) =>
			(a.userName || "").localeCompare(b.userName || "", undefined, {
				sensitivity: "base",
			}),
		)
		.filter((user) => {
			const searchTerms = userSearch
				.trim()
				.toLowerCase()
				.split(/\s+/)
				.filter(Boolean);

			const searchableText = [user.userName, user.userEmail]
				.filter(Boolean)
				.join(" ")
				.toLowerCase();

			return (
				searchTerms.length === 0 ||
				searchTerms.every((term) => searchableText.includes(term))
			);
		});

	const canEdit = canEditCurrentBatch();
	const canDelete = canDeleteCurrentBatch();

	const groupedFilteredPlots = getGroupedFilteredPlots();

	const filteredBatches = batches.filter((item) => {
		const searchTerms = batchSearch
			.trim()
			.toLowerCase()
			.split(/\s+/)
			.filter(Boolean);

		const searchableText = [item.batchName, item.batchOwner]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();

		const matchesSearch =
			searchTerms.length === 0 ||
			searchTerms.every((term) => searchableText.includes(term));

		const matchesOwnerFilter =
			!showOnlyOwnedBatches || item.accessLevel === "O";

		return matchesSearch && matchesOwnerFilter;
	});

	return (
		<div className="p-3">
			<Row className="mb-3">
				<Col>
					<h3 className="mb-0">Stack Batch Editor</h3>
				</Col>
				<Col className="text-end">
					<Button color="primary" onClick={newBatch}>
						+ New Batch
					</Button>
				</Col>
			</Row>

			{error && <Alert color="danger">{error}</Alert>}
			{success && <Alert color="success">{success}</Alert>}

			<Row>
				<Col md="4" lg="3">
					<Card>
						<CardHeader>
							<div className="d-flex justify-content-between align-items-center mb-2">
								<strong>Stack Batches</strong>
								{loadingList && <Spinner size="sm" />}
							</div>

							<Input
								bsSize="sm"
								className="mb-2"
								value={batchSearch}
								onChange={(event) =>
									setBatchSearch(event.target.value)
								}
								placeholder="Search batches..."
							/>

							<FormGroup check className="mb-0">
								<Input
									id="showOnlyOwnedBatches"
									type="checkbox"
									checked={showOnlyOwnedBatches}
									onChange={(event) =>
										setShowOnlyOwnedBatches(
											event.target.checked,
										)
									}
								/>
								<Label check for="showOnlyOwnedBatches">
									My batches only
								</Label>
							</FormGroup>
						</CardHeader>

						<ListGroup flush>
							{filteredBatches.length === 0 && !loadingList && (
								<ListGroupItem className="text-muted">
									No stack batches found.
								</ListGroupItem>
							)}

							{filteredBatches.map((item) => (
								<ListGroupItem
									key={item.batchId}
									action
									active={item.batchId === selectedBatchId}
									onClick={() => selectBatch(item.batchId)}
									style={{ cursor: "pointer" }}
								>
									<div className="d-flex justify-content-between align-items-center">
										<div className="text-truncate">
											<span className="fw-semibold">
												{item.batchName}
											</span>

											{item.batchOwner && (
												<span
													className="ms-2"
													style={{
														color: "var(--ars-neutral-600)",
														fontWeight: 400,
													}}
												>
													by {item.batchOwner}
												</span>
											)}
										</div>

										{getAccessBadge(item.accessLevel)}
									</div>
								</ListGroupItem>
							))}
						</ListGroup>
					</Card>
				</Col>

				<Col md="8" lg="9">
					<Card>
						<CardHeader>
							<strong>
								{batch.batchId
									? "Edit Stack Batch"
									: "New Stack Batch"}
							</strong>

							{dirty && (
								<Badge color="warning" className="ms-2">
									Unsaved changes
								</Badge>
							)}
						</CardHeader>

						<CardBody>
							{loadingBatch ? (
								<Spinner />
							) : (
								<>
									<Form>
										<Row className="mb-3 align-items-center">
											<Col
												md="2"
												className="text-end fw-bold"
											>
												Batch Name:
											</Col>

											<Col md="6">
												<Input
													id="batchName"
													value={batch.batchName}
													disabled={
														batch.batchId &&
														!canEdit
													}
													onChange={(event) =>
														updateField(
															"batchName",
															event.target.value,
														)
													}
													placeholder="Enter batch name"
												/>
											</Col>

											<Col
												md="1"
												className="text-end fw-bold"
											>
												Owner:
											</Col>

											<Col md="3">
												<div className="form-control-plaintext">
													{batch.owner || "-"}
												</div>
											</Col>
										</Row>
										<Row className="mb-4 align-items-center">
											<Col
												md="2"
												className="text-end fw-bold"
											>
												Visibility:
											</Col>

											<Col md="3">
												<FormGroup
													check
													className="mb-0"
												>
													<Input
														id="isPublic"
														type="checkbox"
														checked={batch.isPublic}
														disabled={
															batch.batchId &&
															!canEdit
														}
														onChange={(event) =>
															updateField(
																"isPublic",
																event.target
																	.checked,
															)
														}
													/>
													<Label check for="isPublic">
														Public
													</Label>
												</FormGroup>
											</Col>

											<Col
												md="2"
												className="text-end fw-bold"
											>
												Access:
											</Col>

											<Col md="5">
												{getAccessBadge(
													batch.accessLevel,
												)}
											</Col>
										</Row>
									</Form>

									<div className="d-flex justify-content-between align-items-center mt-3 mb-2">
										<h5 className="mb-0">Stack Plots</h5>
										<Button
											color="primary"
											size="sm"
											disabled={batch.batchId && !canEdit}
											onClick={openPlotModal}
										>
											+ Add Plot
										</Button>
									</div>

									<Table bordered hover responsive>
										<thead>
											<tr>
												<th style={{ width: 50 }} />
												<th style={{ width: 100 }}>
													Site
												</th>
												<th>Plot Name</th>
												<th style={{ width: 120 }}>
													Precision
												</th>
												<th style={{ width: 80 }} />
											</tr>
										</thead>

										<tbody>
											{batch.plots.length === 0 && (
												<tr>
													<td
														colSpan="4"
														className="text-muted text-center"
													>
														No stack plots added.
													</td>
												</tr>
											)}

											{batch.plots.map((plot, index) => (
												<tr key={plot.stackPlotId}>
													<td>
														<ButtonGroup size="sm">
															<Button
																outline
																disabled={
																	index === 0
																}
																disabled={
																	batch.batchId &&
																	!canEdit
																}
																onClick={() =>
																	movePlot(
																		index,
																		-1,
																	)
																}
															>
																↑
															</Button>
															<Button
																outline
																disabled={
																	index ===
																	batch.plots
																		.length -
																		1
																}
																disabled={
																	batch.batchId &&
																	!canEdit
																}
																onClick={() =>
																	movePlot(
																		index,
																		1,
																	)
																}
															>
																↓
															</Button>
														</ButtonGroup>
													</td>

													<td>{plot.siteAbbr}</td>

													<td>{plot.plotName}</td>

													<td>{plot.precision}</td>

													<td className="text-end">
														<Button
															color="danger"
															outline
															size="sm"
															disabled={
																batch.batchId &&
																!canEdit
															}
															onClick={() =>
																removePlot(
																	plot.stackPlotId,
																)
															}
														>
															Remove
														</Button>
													</td>
												</tr>
											))}
										</tbody>
									</Table>

									<div className="text-end">
										{batch.batchId && (
											<Button
												color="danger"
												outline
												className="me-2"
												disabled={
													deleting ||
													(batch.batchId &&
														!canDelete)
												}
												onClick={deleteBatch}
											>
												{deleting
													? "Deleting..."
													: "Delete"}
											</Button>
										)}

										{batch.batchId &&
											canDeleteCurrentBatch() && (
												<Button
													color="primary"
													outline
													className="me-2"
													onClick={
														openPermissionsModal
													}
												>
													Permissions
												</Button>
											)}

										<Button
											color="success"
											disabled={
												saving ||
												!dirty ||
												(batch.batchId && !canEdit)
											}
											onClick={saveBatch}
										>
											{saving ? "Saving..." : "Save"}
										</Button>
									</div>
								</>
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>

			<Modal
				isOpen={plotModalOpen}
				toggle={() => setPlotModalOpen(false)}
				size="lg"
				modalClassName="stack-batch-modal"
			>
				<ModalHeader toggle={() => setPlotModalOpen(false)}>
					Add Stack Plot
				</ModalHeader>

				<ModalBody>
					<Input
						className="mb-3"
						value={plotSearch}
						onChange={(event) => setPlotSearch(event.target.value)}
						placeholder="Search stack plots..."
					/>

					<div className="stack-batch-modal-table-scroll">
						{loadingPlots ? (
							<Spinner />
						) : groupedFilteredPlots.length === 0 ? (
							<div className="text-muted text-center py-3">
								No stack plots found.
							</div>
						) : (
							groupedFilteredPlots.map(([agencyName, plots]) => {
								const collapsed = collapsedAgencies[agencyName];
								const collapsedStyle =
									(collapsed && {
										borderWidth: 0,
										borderRadius:
											"var(--bs-card-inner-border-radius)",
									}) ||
									{};

								return (
									<Card key={agencyName} className="mb-2">
										<CardHeader
											className="stack-batch-agency-header d-flex justify-content-between align-items-center py-2"
											onClick={() =>
												toggleAgencyCollapsed(
													agencyName,
												)
											}
											style={{
												cursor: "pointer",
												...collapsedStyle,
											}}
										>
											<div className="fw-semibold">
												{agencyName}
												<Badge
													color="secondary"
													className="ms-2"
												>
													{plots.length}
												</Badge>
											</div>

											<span>{collapsed ? "+" : "−"}</span>
										</CardHeader>

										{!collapsed && (
											<CardBody className="p-0">
												<Table
													hover
													responsive
													className="mb-0"
												>
													<thead>
														<tr>
															<th>Plot Name</th>
															<th
																style={{
																	width: 120,
																}}
															>
																Site
															</th>
															<th
																style={{
																	width: 120,
																}}
															>
																Precision
															</th>
															<th
																style={{
																	width: 100,
																}}
															/>
														</tr>
													</thead>

													<tbody>
														{plots.map((plot) => {
															const added =
																batch.plots.some(
																	(
																		existingPlot,
																	) =>
																		existingPlot.stackPlotId ===
																		plot.stackPlotId,
																);

															return (
																<tr
																	key={
																		plot.stackPlotId
																	}
																>
																	<td>
																		{
																			plot.plotName
																		}
																	</td>

																	<td>
																		<Badge color="info">
																			{
																				plot.siteAbbr
																			}
																		</Badge>
																	</td>

																	<td>
																		{
																			plot.precision
																		}
																	</td>

																	<td className="text-end">
																		<Button
																			size="sm"
																			color={
																				added
																					? "secondary"
																					: "primary"
																			}
																			disabled={
																				added
																			}
																			onClick={() =>
																				addPlot(
																					plot,
																				)
																			}
																		>
																			{added
																				? "Added"
																				: "Add"}
																		</Button>
																	</td>
																</tr>
															);
														})}
													</tbody>
												</Table>
											</CardBody>
										)}
									</Card>
								);
							})
						)}
					</div>
				</ModalBody>

				<ModalFooter>
					<Button
						color="secondary"
						onClick={() => setPlotModalOpen(false)}
					>
						Close
					</Button>
				</ModalFooter>
			</Modal>

			<Modal
				isOpen={permissionsModalOpen}
				toggle={() => setPermissionsModalOpen(false)}
				size="lg"
				modalClassName="stack-batch-modal"
			>
				<ModalHeader toggle={() => setPermissionsModalOpen(false)}>
					Manage Permissions
				</ModalHeader>

				<ModalBody>
					<div className="mb-3">
						<strong>{batch.batchName}</strong>
					</div>

					<Input
						className="mb-3"
						value={userSearch}
						onChange={(event) => setUserSearch(event.target.value)}
						placeholder="Search users..."
					/>

					<div className="stack-batch-modal-table-scroll">
						{loadingPermissions ? (
							<Spinner />
						) : (
							<Table hover responsive>
								<thead>
									<tr>
										<th>User</th>
										<th>Email</th>
										<th style={{ width: 180 }}>Access</th>
									</tr>
								</thead>

								<tbody>
									{filteredUsers.map((user) => {
										const userId = user.userId || user.id;
										const currentPermission =
											getUserPermission(userId);
										const isOwnerPermission =
											currentPermission === "O";

										return (
											<tr key={userId}>
												<td>{user.userName}</td>

												<td>{user.userEmail}</td>
												<td>
													<Input
														type="select"
														value={
															currentPermission
														}
														disabled={
															isOwnerPermission
														}
														onChange={(event) =>
															updateUserPermission(
																user,
																event.target
																	.value,
															)
														}
													>
														<option value="">
															None
														</option>
														<option value="R">
															Read
														</option>
														<option value="W">
															Write
														</option>
														{isOwnerPermission && (
															<option value="O">
																Owner
															</option>
														)}
													</Input>
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						)}
					</div>
				</ModalBody>

				<ModalFooter>
					<Button
						color="secondary"
						outline
						onClick={() => setPermissionsModalOpen(false)}
					>
						Cancel
					</Button>

					<Button
						color="primary"
						disabled={savingPermissions}
						onClick={savePermissions}
					>
						{savingPermissions ? "Saving..." : "Save Permissions"}
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
}
